import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { PredictionWithMatch } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyGroups } from '@/hooks/useMyGroups';
import { fallbackScopeFor, useActiveGroup, useSetActiveGroup } from '@/state/activeGroup';
import { cn } from '@/lib/cn';
import { STAGE_LABELS, formatKickoff, matchFifa } from '@/lib/format';
import type { StandingsRow, StandingsResponse } from '@/components/MatchStandings';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';
import { CrownedFlag } from '@/components/CrownedFlag';
import { TierBadge } from '@/components/TierBadge';

export function StandingsPage() {
  const me = useCurrentUser();
  const groupsQuery = useMyGroups();
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();

  // Which row is currently expanded to show that user's past predictions.
  // Null = all collapsed. Only one row expanded at a time to keep the page
  // scannable.
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const groups = groupsQuery.data?.groups ?? [];
  const isAdmin = me?.role === 'admin';
  // `useMyGroups` is gated on `me`, so while `useCurrentUser` is still
  // resolving the groups query is disabled (isLoading=false, data=undefined).
  // Treat that window as loading too so the page doesn't flicker.
  const groupsLoading = me === undefined || groupsQuery.isLoading;

  // Reconcile a persisted scope that is no longer valid for this viewer:
  //   - non-admins on `'global'` (now admin-only) drop to first group / self
  //   - any user on a group id they're no longer in drops to the same fallback
  //   - non-admins stuck on `'self'` after joining a group auto-promote into it
  // Runs after first paint — the query may 403 for one render before this
  // fires, which is fine; the 403 effect below also catches that race.
  useEffect(() => {
    if (me === undefined) return;
    if (!groupsQuery.data) return;
    const fallback = fallbackScopeFor(!!isAdmin, groups.map((g) => g.id));
    if (activeGroupId === 'global') {
      if (!isAdmin) setActiveGroupId(fallback);
      return;
    }
    if (activeGroupId === 'self') {
      if (groups.length > 0) setActiveGroupId(groups[0]!.id);
      return;
    }
    const stillExists = groups.some((g) => g.id === activeGroupId);
    if (!stillExists) setActiveGroupId(fallback);
  }, [me, isAdmin, groupsQuery.data, groups, activeGroupId, setActiveGroupId]);

  // Scope label above the page title. Mirrors the resolved scope: 'Global'
  // for admins viewing everyone, the group name for a group scope, and
  // 'Just me' when a non-admin without groups sees only themselves.
  const scopeLabel =
    activeGroupId === 'global'
      ? 'Global'
      : activeGroupId === 'self'
        ? 'Just me'
        : (groups.find((g) => g.id === activeGroupId)?.name ?? 'Just me');

  const scopeId = activeGroupId;

  const query = useQuery({
    queryKey: ['standings', 'overall', scopeId],
    queryFn: () =>
      api.get<StandingsResponse>(
        `/api/standings?groupId=${encodeURIComponent(scopeId)}`,
      ),
    refetchInterval: 30_000,
  });

  // If the persisted groupId became stale (e.g. the user was removed from
  // that group elsewhere) the server returns 403 FORBIDDEN. Drop to the
  // role-appropriate fallback — admin → global, otherwise first group or
  // 'self'. The key change auto-refetches.
  useEffect(() => {
    if (!query.isError) return;
    const err = query.error;
    if (err instanceof ApiError && err.status === 403 && err.code === 'FORBIDDEN') {
      const fallback = fallbackScopeFor(!!isAdmin, groups.map((g) => g.id));
      if (activeGroupId !== fallback) setActiveGroupId(fallback);
    }
  }, [query.isError, query.error, activeGroupId, isAdmin, groups, setActiveGroupId]);

  const rows = query.data?.standings ?? [];
  const totalUsers = query.data?.total ?? rows.length;
  const myRow = me ? (rows.find((r) => r.userId === me.id) ?? null) : null;
  const myPosition = myRow?.position ?? null;

  const totalExact = rows.reduce((n, r) => n + r.exactCount, 0);
  const liveTotal = rows.reduce((n, r) => n + (r.livePoints ?? 0), 0);

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <div className="k">Viewing · {scopeLabel}</div>
          <h1>
            The <span className="emph">table</span>.
          </h1>
        </div>
      </div>

      <StandingsScopeSelector />

      {groupsLoading ? (
        <Spinner fullScreen />
      ) : (
        <>
          <div className="rail-stats" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="stat">
              <div className="k">Your rank</div>
              <div className="v">
                {myPosition !== null ? `#${myPosition}` : '—'}
                {myPosition !== null ? (
                  <span className="unit">of {totalUsers}</span>
                ) : null}
              </div>
            </div>
            <div className="stat">
              <div className="k">Total points</div>
              <div
                className="v"
                style={
                  (myRow?.livePoints ?? 0) > 0 ? { color: 'var(--turf)' } : undefined
                }
              >
                {(myRow?.totalPoints ?? 0) + (myRow?.livePoints ?? 0)}
                {(myRow?.livePoints ?? 0) > 0 ? (
                  <span className="unit">+{myRow!.livePoints} live</span>
                ) : null}
              </div>
            </div>
            <div className="stat">
              <div className="k">Exact scores</div>
              <div className="v">
                {myRow?.exactCount ?? 0}
                <span className="unit">× 3 pts</span>
              </div>
            </div>
            <div className="stat">
              <div className="k">Pool exacts</div>
              <div className="v">
                {totalExact}
                {liveTotal > 0 ? <span className="unit">+{liveTotal} live</span> : null}
              </div>
            </div>
          </div>

          {query.isLoading ? (
            <Spinner fullScreen />
          ) : query.isError ? (
            <p
              style={{
                padding: '48px 0',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--danger)',
              }}
            >
              Couldn't load the standings.
            </p>
          ) : rows.length === 0 ? (
            <p
              style={{
                padding: '48px 0',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--fg-muted)',
              }}
            >
              No scored predictions yet. The board fills in as matches finish.
            </p>
          ) : (
            <div className="matches" style={{ gap: 8 }}>
              {rows.map((row) => {
                const position = row.position;
                const isMe = me && row.userId === me.id;
                const expanded = expandedUserId === row.userId;
                const rankColor =
                  position === 1
                    ? 'var(--sun)'
                    : position === 2
                      ? 'oklch(0.80 0.02 60)'
                      : position === 3
                        ? 'oklch(0.65 0.10 50)'
                        : 'var(--line)';
                return (
                  <div
                    key={row.userId}
                    style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedUserId((prev) =>
                          prev === row.userId ? null : row.userId,
                        )
                      }
                      aria-expanded={expanded}
                      aria-controls={`lb-expand-${row.userId}`}
                      className={cn('match-card', isMe && 'is-me')}
                      style={{
                        cursor: 'pointer',
                        gridTemplateColumns: 'auto auto 1fr auto auto',
                        gap: 18,
                        background: 'transparent',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'inherit',
                        width: '100%',
                        // Tighter bottom radius when expanded so the panel
                        // reads as a continuation of the row.
                        borderBottomLeftRadius: expanded ? 0 : undefined,
                        borderBottomRightRadius: expanded ? 0 : undefined,
                      }}
                    >
                      <div className="accent-edge" style={{ background: rankColor }} />
                      <div
                        className="bebas tabular"
                        style={{
                          fontSize: 36,
                          width: 52,
                          textAlign: 'center',
                          color: position <= 3 ? rankColor : 'var(--fg-dim)',
                          lineHeight: 1,
                        }}
                      >
                        {position}
                      </div>
                      <CrownedFlag
                        crowned={position === 1}
                        url={row.favoriteTeamFlagUrl}
                        name={row.displayName}
                        size="sm"
                      />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.displayName}
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              marginLeft: 4,
                              verticalAlign: 'middle',
                            }}
                          >
                            <TierBadge tier={row.paymentTier} />
                          </span>
                          {isMe ? (
                            <span
                              className="mono"
                              style={{
                                fontSize: 10,
                                background:
                                  'color-mix(in oklab, var(--team-accent) 18%, transparent)',
                                color: 'var(--team-accent)',
                                padding: '2px 8px',
                                borderRadius: 999,
                                marginLeft: 8,
                                letterSpacing: '0.12em',
                                fontWeight: 700,
                              }}
                            >
                              YOU
                            </span>
                          ) : null}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                          {row.matchesScored} match
                          {row.matchesScored === 1 ? '' : 'es'} · {row.scoredCount} scored ({row.exactCount} exact)
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 2,
                        }}
                      >
                        <div
                          className="bebas tabular"
                          style={{
                            fontSize: 36,
                            lineHeight: 0.95,
                            color: (row.livePoints ?? 0) > 0 ? 'var(--turf)' : undefined,
                          }}
                        >
                          {row.totalPoints + (row.livePoints ?? 0)}
                        </div>
                        {(row.livePoints ?? 0) > 0 ? (
                          <div
                            className="mono"
                            style={{
                              fontSize: 11,
                              color: 'var(--turf)',
                              fontWeight: 700,
                            }}
                          >
                            +{row.livePoints} live
                          </div>
                        ) : null}
                      </div>
                      <div
                        aria-hidden
                        style={{
                          fontSize: 14,
                          color: 'var(--fg-muted)',
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 120ms ease-out',
                          lineHeight: 1,
                        }}
                      >
                        ▾
                      </div>
                    </button>
                    {expanded ? (
                      <UserPredictionsPanel
                        id={`lb-expand-${row.userId}`}
                        userId={row.userId}
                        displayName={row.displayName}
                        scopeId={scopeId}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * Dropdown that scopes the standings query.
 *   - Admins see Global plus every group they're in (Global is admin-only).
 *   - Non-admins see only the groups they're in.
 *   - Hidden entirely when there's just one option (the page header already
 *     announces the scope, e.g. "Viewing · Just me" for a no-groups user).
 */
function StandingsScopeSelector() {
  const me = useCurrentUser();
  const query = useMyGroups();
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();

  const groups = useMemo(() => query.data?.groups ?? [], [query.data]);
  const isAdmin = me?.role === 'admin';

  // Hold the dropdown back until both `me` and the groups query have settled
  // — otherwise it pops in/out on first paint for users with groups, or
  // renders the wrong options before role/membership is known.
  if (me === undefined) return null;
  if (!query.data) return null;
  // With no groups there's never more than one option (Global for admins,
  // 'self' for everyone else) — and the page header already labels it.
  if (groups.length === 0) return null;

  return (
    <label
      style={{
        display: 'flex',
        minWidth: 0,
        marginBottom: 12,
        maxWidth: 360,
      }}
    >
      <span
        className="sr-only"
        style={{ position: 'absolute', left: -9999 }}
      >
        Standings scope
      </span>
      <select
        value={activeGroupId}
        onChange={(e) => setActiveGroupId(e.target.value)}
        // iOS Safari needs ≥16px font-size to suppress auto-zoom on focus.
        // Height grows to 44px below md to hit the WCAG tap-target minimum;
        // desktop keeps the trimmer 36px pill.
        className="max-md:!h-11 max-md:!text-base"
        style={{
          flex: 1,
          minWidth: 0,
          height: 36,
          padding: '0 34px 0 14px',
          borderRadius: 999,
          border: '1px solid var(--line)',
          background: 'var(--surface)',
          color: 'var(--fg)',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          letterSpacing: '0.02em',
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5 L6 6.5 L11 1.5'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          backgroundSize: '10px 7px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {isAdmin ? <option value="global">Global</option> : null}
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name} ({g.ownerDisplayName})
          </option>
        ))}
      </select>
    </label>
  );
}

interface UserPredictionsResponse {
  predictions: PredictionWithMatch[];
}

function UserPredictionsPanel({
  id,
  userId,
  displayName,
  scopeId,
}: {
  id: string;
  userId: string;
  displayName: string;
  scopeId: string;
}) {
  const query = useQuery({
    queryKey: ['predictions', 'user', userId, scopeId],
    queryFn: () =>
      api.get<UserPredictionsResponse>(
        `/api/predictions/user/${userId}?groupId=${encodeURIComponent(scopeId)}`,
      ),
  });

  return (
    <div
      id={id}
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--line)',
        borderTop: 'none',
        borderBottomLeftRadius: 'var(--radius)',
        borderBottomRightRadius: 'var(--radius)',
        padding: '12px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {query.isLoading ? (
        <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'center' }}>
          <Spinner size="sm" />
        </div>
      ) : query.isError ? (
        <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>
          Couldn't load {displayName}'s predictions.
        </p>
      ) : (query.data?.predictions.length ?? 0) === 0 ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--fg-muted)',
            margin: 0,
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          No revealed predictions yet — picks unlock at kickoff.
        </p>
      ) : (
        query.data!.predictions.map((p) => (
          <PredictionRow key={p.id} pwm={p} />
        ))
      )}
    </div>
  );
}

function PredictionRow({ pwm }: { pwm: PredictionWithMatch }) {
  const { match, prediction, pointsAwarded } = pwm;
  const fifa = matchFifa(match);
  const result = match.result;
  const resultFt = result?.fullTime;
  const resultEt = result?.extraTime;
  const resultPens = result?.penalties;
  return (
    <Link
      to={`/matches/${match.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 12,
        alignItems: 'center',
        padding: '10px 12px',
        borderRadius: 10,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--fg-muted)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {match.group ? `Group ${match.group}` : STAGE_LABELS[match.stage]} ·{' '}
          {formatKickoff(match.kickoffUtc)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <TeamFlag
            url={match.team1?.flagUrl ?? null}
            name={fifa.t1}
            size="xs"
          />
          <span className="tabular">{fifa.t1}</span>
          <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>vs</span>
          <span className="tabular">{fifa.t2}</span>
          <TeamFlag
            url={match.team2?.flagUrl ?? null}
            name={fifa.t2}
            size="xs"
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 2,
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
        }}
      >
        <span>
          <span style={{ color: 'var(--fg-muted)', marginRight: 6 }}>pick</span>
          {prediction.fullTime.team1}–{prediction.fullTime.team2}
          {prediction.extraTime
            ? ` · ET ${prediction.extraTime.team1}–${prediction.extraTime.team2}`
            : ''}
          {prediction.penalties
            ? ` · Pens ${prediction.penalties.team1}–${prediction.penalties.team2}`
            : ''}
        </span>
        {resultFt ? (
          <span style={{ color: 'var(--fg-muted)' }}>
            actual {resultFt.team1}–{resultFt.team2}
            {resultEt ? ` · ET ${resultEt.team1}–${resultEt.team2}` : ''}
            {resultPens ? ` · Pens ${resultPens.team1}–${resultPens.team2}` : ''}
          </span>
        ) : (
          <span style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
            not yet scored
          </span>
        )}
      </div>
      <div
        style={{
          minWidth: 44,
          textAlign: 'center',
          padding: '4px 8px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          background:
            pointsAwarded == null
              ? 'var(--surface-2)'
              : pointsAwarded >= 3
                ? 'color-mix(in oklab, var(--turf) 20%, transparent)'
                : pointsAwarded >= 1
                  ? 'color-mix(in oklab, var(--sun) 22%, transparent)'
                  : 'var(--bg-elevated)',
          color:
            pointsAwarded == null
              ? 'var(--fg-muted)'
              : pointsAwarded >= 3
                ? 'var(--turf)'
                : pointsAwarded >= 1
                  ? 'var(--sun)'
                  : 'var(--fg-muted)',
        }}
      >
        {pointsAwarded == null ? '—' : `+${pointsAwarded}`}
      </div>
    </Link>
  );
}
